import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import useCart from '../../hooks/useCart';
import { useAuth } from '../../context/AuthContext';
import { useRestaurantContext } from '../../context/RestaurantContext';
import MoneyUtils from '../../utils/moneyUtils';
import { capitalizeFirst } from '../../utils/stringUtils';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';
import AlertDialog from '../../components/dialogs/AlertDialog';
import { OffersSheet, PromotionBannerCarousel } from '../../components/PromotionComponents';
import { formatPromoLabel, calcPromoDiscount } from '../../utils/promoUtils';
import { orderUtils } from '../../utils/orderUtils';
import { getPromotions, createPaymentOrder, verifyPayment, getPaymentStatus, updateOrderStatus, createCartHold } from '../../services/api/restApiAdapter';
import { listenToCartHoldStatus } from '../../services/api/firebaseAdapter';
import confetti from 'canvas-confetti';
import PaymentStatusSheet from './components/PaymentStatusSheet';
import DeliveryAddressSection from './components/DeliveryAddressSection';

// ─── Razorpay Loader ──────────────────────────────────────────────────────────
// Dynamically loads the Razorpay checkout.js SDK when needed.
const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

// ─── Small Static Sub-Components ──────────────────────────────────────────────

const BillRow = ({ label, value, isBold = false, isTotal = false, color }) => (
  <div className={`flex justify-between items-center py-1 ${isTotal ? 'pt-3 mt-2 border-t border-dashed border-gray-300' : ''}`}>
    <span className={`text-sm ${isBold ? 'font-bold text-gray-900' : 'text-gray-500'} ${isTotal ? 'text-lg' : ''}`}>{label}</span>
    <span className={`text-sm ${isBold ? 'font-bold text-gray-900' : 'text-gray-900'} ${isTotal ? 'text-lg' : ''} ${color ? color : ''}`}>{value}</span>
  </div>
);

const TipChip = ({ amount, selected, onClick, label }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
      selected 
        ? 'bg-black text-white shadow-md' 
        : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
    }`}
  >
    {label || MoneyUtils.format(amount)}
  </button>
);

// ─── Email Gate Modal ──────────────────────────────────────────────────────────
// Shown when requireSignIn is true (Order mode) or in Pay & Order mode.
// `allowSkip` renders an optional Skip link.

const EmailGateModal = ({ isOpen, onConfirm, onSkip, allowSkip = false, theme }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    setError('');
    onConfirm(email.trim());
  };

  const accentColor = theme?.secondary || '#000';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md bg-white rounded-t-3xl p-6 pb-10 shadow-2xl">
        <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-6" />
        <div className="text-3xl mb-3 text-center">📧</div>
        <h2 className="text-lg font-bold text-gray-900 text-center mb-1">Enter Your Email</h2>
        <p className="text-sm text-gray-500 text-center mb-5">
          We'll use this to keep you updated on your order.
        </p>

        <input
          type="email"
          autoFocus
          value={email}
          onChange={(e) => { setEmail(e.target.value); setError(''); }}
          placeholder="you@example.com"
          className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:outline-none mb-2"
          style={{ '--tw-ring-color': accentColor }}
          onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
        />
        {error && <p className="text-xs text-red-500 mb-3">{error}</p>}

        <button
          onClick={handleConfirm}
          className="w-full py-3.5 rounded-xl text-sm font-bold transition-all shadow-sm mt-2"
          style={{ backgroundColor: accentColor, color: theme?.primary || '#fff' }}
        >
          Continue
        </button>

        {allowSkip && (
          <button
            onClick={onSkip}
            className="w-full py-2 text-sm text-gray-400 hover:text-gray-600 mt-3 transition-colors"
          >
            Skip for now
          </button>
        )}
      </div>
    </div>
  );
};

// PayAndOrderStub removed — replaced by real Razorpay checkout triggered inline.



// Payment confirmation is now handled by the modular PaymentStatusSheet component.

// ─── Main Checkout Flow ────────────────────────────────────────────────────────

const CheckoutFlow = () => {
  const navigate = useNavigate();
  const { cartData, submitOrder, updateQuantity, removeItem, clearCart } = useCart();
  const { ensureGuestAuth, signInAsGuestWithEmail, capturedEmail, setCapturedEmail } = useAuth();
  const {
    restaurantId,
    tableDetails,
    tableNumber,
    isTrackedTable,
    tableSection,
    initialOrderType,
    capabilities,
    theme,
    details: restaurantDetails,
    selfOrderConfig,
  } = useRestaurantContext();

  const requireSignIn = selfOrderConfig?.requireSignIn || false;
  const allowPayAndOrderWithoutSignIn = selfOrderConfig?.allowPayAndOrderWithoutSignIn ?? true;

  // Determine available order type options based on context:
  // 1. If scanned from a Table QR: Home Delivery is hidden! Only [Dine-In, Pickup]
  // 2. If entered via delivery link: [Delivery, Pickup]
  // 3. If entered via pickup link: [Pickup, Dine-In]
  // 4. Otherwise: All enabled types [Dine-In, Delivery, Pickup]
  const availableOrderTypes = React.useMemo(() => {
    if (tableNumber) {
      return ['dine_in', 'takeaway'];
    }
    if (initialOrderType === 'delivery') {
      return ['delivery', 'takeaway'];
    }
    if (initialOrderType === 'takeaway') {
      return ['takeaway', 'dine_in'];
    }
    return ['dine_in', 'delivery', 'takeaway'];
  }, [tableNumber, initialOrderType]);

  const [deliveryType, setDeliveryType] = useState(() => {
    if (tableNumber) return 'dine_in';
    if (initialOrderType && ['dine_in', 'takeaway', 'delivery'].includes(initialOrderType)) {
      return initialOrderType;
    }
    return 'dine_in';
  });

  // Ensure deliveryType stays within availableOrderTypes if context changes
  useEffect(() => {
    if (tableNumber && deliveryType === 'delivery') {
      setDeliveryType('dine_in');
    }
  }, [tableNumber, deliveryType]);
  const [tipAmount, setTipAmount] = useState(0);
  const [customTip, setCustomTip] = useState('');
  const [phone, setPhone] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('online');

  const [error, setError] = useState(null);
  const [processingAction, setProcessingAction] = useState(null);
  const [submittedOrder, setSubmittedOrder] = useState(null);

  // Payment confirmation sheet state: null | 'verifying' | 'success' | 'failed' | 'delayed'
  const [paymentConfirmState, setPaymentConfirmState] = useState(null);
  const [paymentErrorMessage, setPaymentErrorMessage] = useState('');
  const [confirmedOrder, setConfirmedOrder] = useState(null);
  // true = Razorpay paid flow, false = COD / Pay-at-Counter flow
  const [wasPaidFlow, setWasPaidFlow] = useState(false);

  // Promotions state
  const [promotions, setPromotions] = useState([]);
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [isOffersSheetOpen, setIsOffersSheetOpen] = useState(false);

  // Email gate modal (for Order mode with requireSignIn, and PayAndOrder mode)
  const [showEmailGate, setShowEmailGate] = useState(false);
  const [emailGateAction, setEmailGateAction] = useState(null); // 'order' | 'payAndOrder'

  // Cart Hold (Show Code) state
  const [cartHoldCode, setCartHoldCode] = useState(null);
  const [activeCartHoldId, setActiveCartHoldId] = useState(null);
  const [isClaimedSuccess, setIsClaimedSuccess] = useState(false);

  // Listen to cart hold status in real-time
  useEffect(() => {
    if (!activeCartHoldId || !restaurantId) return;

    let timeoutId;

    const unsubscribe = listenToCartHoldStatus(restaurantId, activeCartHoldId, (result) => {
      if (result.success && result.data?.status === 'claimed') {
        setIsClaimedSuccess(true);
        clearCart();

        // Fire small amount of confetti
        confetti({
          particleCount: 50,
          spread: 60,
          origin: { y: 0.6 },
          zIndex: 9999
        });

        // Auto close after 5 seconds
        timeoutId = setTimeout(() => {
          setCartHoldCode(null);
          setActiveCartHoldId(null);
          setIsClaimedSuccess(false);
          navigate(`/restaurant/${restaurantId}`, { replace: true });
        }, 5000);
      }
    });

    return () => {
      unsubscribe();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [activeCartHoldId, restaurantId, clearCart, navigate]);

  useEffect(() => {
    if (cartData.items.length === 0 && !submittedOrder && !paymentConfirmState && !confirmedOrder && !cartHoldCode) {
      navigate('../', { replace: true, relative: 'path' });
    }
  }, [cartData.items, navigate, submittedOrder, paymentConfirmState, confirmedOrder, cartHoldCode]);

  // Fetch promotions when restaurantId is ready
  useEffect(() => {
    if (!restaurantId) return;
    getPromotions(restaurantId).then((result) => {
      if (result.success) setPromotions(result.data || []);
    });
  }, [restaurantId]);

  // Validate applied promo against min order amount whenever subtotal changes
  useEffect(() => {
    if (appliedPromo && appliedPromo.minOrderAmount > 0 && cartData.subtotal < appliedPromo.minOrderAmount) {
      setAppliedPromo(null);
    }
  }, [cartData.subtotal, appliedPromo]);

  const platformFee = 0;
  const taxAmount = cartData.tax || 0;
  const deliveryFee = deliveryType === 'delivery' ? (restaurantDetails?.delivery_fee || 40) : 0;

  // Promotion discount (client-side preview; server re-validates)
  const discountAmount = calcPromoDiscount(cartData.subtotal, appliedPromo);

  const grandTotal = cartData.subtotal + taxAmount + deliveryFee + tipAmount + platformFee - discountAmount;

  const handleApplyPromo = (promo) => {
    setAppliedPromo(promo);
  };

  // ─── Core order submission ──────────────────────────────────────────────────
  const doSubmitOrder = async (overrideEmail) => {
    if (deliveryType === 'delivery') {
      const cleanPhone = String(phone || '').replace(/\D/g, '');
      if (!cleanPhone || cleanPhone.length < 10) {
        setError("Please enter a valid 10-digit mobile number for delivery.");
        return;
      }
      if (!recipientName.trim()) {
        setError("Please enter the recipient's name for delivery.");
        return;
      }
      if (!deliveryAddress || (!deliveryAddress.addressLine1 && !deliveryAddress.address)) {
        setError("Please add or select a delivery address.");
        return;
      }
    }

    setProcessingAction('order');
    try {
      const user = await ensureGuestAuth();
      const emailToSend = overrideEmail || capturedEmail || null;

      const orderPayload = {
        user_id: user.uid,
        restaurant_id: restaurantId,
        items: cartData.items.map(item => ({
          menu_item_id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          customizations: item.customizations || null,
          taxRuleId: item.taxRuleId || null,
          category: item.category || null,
        })),
        orderType: deliveryType,
        tableId: deliveryType === 'dine_in' ? (tableDetails?.id || null) : null,
        tableNumber: deliveryType === 'dine_in' ? (tableDetails?.name || tableNumber || null) : null,
        tableName: deliveryType === 'dine_in' ? (tableDetails?.name || null) : null,
        tableLocation: deliveryType === 'dine_in' ? ([tableDetails?.tableLocation, tableDetails?.section].filter(Boolean).join(' • ') || tableSection || null) : null,
        isTrackedTable: deliveryType === 'dine_in' ? (tableDetails ? !tableDetails.isUntracked : isTrackedTable) : true,
        tipAmount: tipAmount,
        deliveryFee: deliveryFee,
        paymentMethod: 'cod',
        paymentStatus: 'pending',
        orderStatus: 'pending',
        phone: phone || null,
        customerName: recipientName.trim() || null,
        email: emailToSend,
        promotionId: appliedPromo ? appliedPromo.id : null,
        deliveryAddress: deliveryType === 'delivery' ? deliveryAddress : null,
      };

      const result = await submitOrder(orderPayload);
      if (result.success) {
        setWasPaidFlow(false); // COD / Pay-at-Counter — no payment received yet
        setConfirmedOrder(result.data);
        setPaymentConfirmState('success');
        clearCart?.();

        // Fire celebratory confetti
        confetti({
          particleCount: 70,
          spread: 70,
          origin: { y: 0.6 },
          zIndex: 9999
        });
      } else {
        throw new Error(result.error || "Failed to place order");
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setProcessingAction(null);
    }
  };

  // ─── CTA handlers per mode ──────────────────────────────────────────────────

  // "Order" mode: optionally requires email first
  const handleOrderModeAction = async () => {
    if (requireSignIn && !capturedEmail) {
      setEmailGateAction('order');
      setShowEmailGate(true);
      return;
    }
    await doSubmitOrder();
  };

  // Email gate confirmed (Order mode)
  const handleEmailConfirmed = async (email) => {
    await signInAsGuestWithEmail(email);
    setShowEmailGate(false);
    await doSubmitOrder(email);
  };

  // ─── Show Code mode launcher ────────────────────────────────────────────────
  const handleShowCodeAction = async () => {
    setProcessingAction('showCode');
    setError(null);
    try {
      const payload = {
        items: cartData.items.map((item) => ({
          menuItemId: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          customizations: item.customizations || null,
          cookingInstruction: item.specialInstructions || null,
          category: item.category || null,
          taxRuleId: item.taxRuleId || null,
        })),
        tableId: deliveryType === 'dine_in' ? (tableDetails?.id || null) : null,
        tableNumber: deliveryType === 'dine_in' ? (tableDetails?.name || tableNumber || null) : null,
        tableName: deliveryType === 'dine_in' ? (tableDetails?.name || null) : null,
        tableLocation: deliveryType === 'dine_in' ? ([tableDetails?.tableLocation, tableDetails?.section].filter(Boolean).join(' • ') || tableSection || null) : null,
        isTrackedTable: deliveryType === 'dine_in' ? (tableDetails ? !tableDetails.isUntracked : isTrackedTable) : true,
      };

      const result = await createCartHold(restaurantId, payload);
      if (result.success && result.data?.code) {
        setCartHoldCode(result.data.code);
        setActiveCartHoldId(result.data.cartId);
      } else {
        throw new Error(result.error || 'Failed to generate pickup code');
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setProcessingAction(null);
    }
  };

  // ─── Razorpay checkout launcher ─────────────────────────────────────────────
  const launchRazorpayCheckout = async () => {
    if (deliveryType === 'delivery') {
      const cleanPhone = String(phone || '').replace(/\D/g, '');
      if (!cleanPhone || cleanPhone.length < 10) {
        setError("Please enter a valid 10-digit mobile number for delivery.");
        return;
      }
      if (!recipientName.trim()) {
        setError("Please enter the recipient's name for delivery.");
        return;
      }
      if (!deliveryAddress || (!deliveryAddress.addressLine1 && !deliveryAddress.address)) {
        setError("Please add or select a delivery address.");
        return;
      }
    }

    setProcessingAction('payAndOrder');
    setWasPaidFlow(true); // Razorpay paid flow — actual payment will be collected
    setError(null);
    setPaymentErrorMessage('');
    try {
      const user = await ensureGuestAuth();

      // Build cart payload (same shape as submitOrder)
      const payload = {
        items: cartData.items.map((item) => ({
          menuItemId: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          customizations: item.customizations || null,
          cookingInstruction: item.specialInstructions || null,
          category: item.category || null,
          taxRuleId: item.taxRuleId || null,
        })),
        orderType: deliveryType,
        tableId: deliveryType === 'dine_in' ? (tableDetails?.id || null) : null,
        tableNumber: deliveryType === 'dine_in' ? (tableDetails?.name || tableNumber || null) : null,
        tableName: deliveryType === 'dine_in' ? (tableDetails?.name || null) : null,
        tableLocation: deliveryType === 'dine_in' ? ([tableDetails?.tableLocation, tableDetails?.section].filter(Boolean).join(' • ') || tableSection || null) : null,
        isTrackedTable: deliveryType === 'dine_in' ? (tableDetails ? !tableDetails.isUntracked : isTrackedTable) : true,
        phone: phone || null,
        customerName: recipientName.trim() || null,
        deliveryFee: deliveryFee || 0,
        tipAmount: tipAmount || 0,
        promotionId: appliedPromo ? appliedPromo.id : null,
        promoCode: appliedPromo ? appliedPromo.code : null,
        deliveryAddress: deliveryType === 'delivery' ? deliveryAddress : null,
      };

      // Step 1: Create Razorpay order on server (saves pending order in MongoDB)
      const orderResult = await createPaymentOrder(restaurantId, payload);
      if (!orderResult.success) throw new Error(orderResult.error || 'Payment initiation failed');

      const { gatewayOrderId, keyId, amount, currency, orderId } = orderResult.data;

      // Step 2: Load Razorpay SDK
      const loaded = await loadRazorpay();
      if (!loaded) throw new Error('Razorpay SDK failed to load. Check your connection.');

      // Step 3: Open Razorpay checkout modal
      const rzp = new window.Razorpay({
        key: keyId || import.meta.env.VITE_RAZORPAY_KEY_ID,
        amount,
        currency: currency || 'INR',
        order_id: gatewayOrderId,
        name: restaurantDetails?.name || 'Restaurant',
        description: `Order #${orderId?.slice(-6).toUpperCase()}`,
        prefill: {
          email: capturedEmail || '',
          contact: phone || '',
        },
        theme: { color: theme?.secondary || '#000000' },
        handler: async (rzpResponse) => {
          // ── Step 1: Show verifying bottomsheet immediately ───────────────────
          setProcessingAction(null);
          setPaymentConfirmState('verifying');

          // ── Step 2: Verify signature (cryptographic proof from Razorpay) ───
          const verifyResult = await verifyPayment(restaurantId, {
            razorpay_payment_id: rzpResponse.razorpay_payment_id,
            razorpay_order_id:   rzpResponse.razorpay_order_id,
            razorpay_signature:  rzpResponse.razorpay_signature,
            orderId,
          });

          if (!verifyResult.success) {
            // Signature mismatch — show failed sheet with realistic refund message
            setPaymentErrorMessage(
              'Payment signature verification failed. If any amount was debited, your bank will automatically refund it within 5-7 business days.'
            );
            setPaymentConfirmState('failed');
            return;
          }

          // Cryptographically verified → immediately clear cart so no items linger
          clearCart?.();

          // ── Step 3: Poll until server confirms (webhook sync + Bill + Payment) ─
          const MIN_VERIFY_DISPLAY_MS = 3500;
          const verifyStartTime = Date.now();
          const MAX_WAIT_MS = 25000;
          const INTERVAL_MS = 2000;
          let pollFinished = false;

          while (Date.now() - verifyStartTime < MAX_WAIT_MS) {
            const statusResult = await getPaymentStatus(restaurantId, orderId);
            if (statusResult.success) {
              const statusData = statusResult.data;

              // Check for completed payment
              if (statusData?.fullyConfirmed || statusData?.paymentStatus === 'completed') {
                // Ensure verifying animation shows for at least 3.5 seconds
                const elapsed = Date.now() - verifyStartTime;
                if (elapsed < MIN_VERIFY_DISPLAY_MS) {
                  await new Promise((r) => setTimeout(r, MIN_VERIFY_DISPLAY_MS - elapsed));
                }

                setConfirmedOrder(statusData);
                setPaymentConfirmState('success');
                clearCart?.();
                pollFinished = true;

                // Fire celebratory confetti
                confetti({
                  particleCount: 70,
                  spread: 70,
                  origin: { y: 0.6 },
                  zIndex: 9999,
                });
                break;
              }

              // Check for explicit failure
              if (statusData?.paymentStatus === 'failed') {
                const elapsed = Date.now() - verifyStartTime;
                if (elapsed < MIN_VERIFY_DISPLAY_MS) {
                  await new Promise((r) => setTimeout(r, MIN_VERIFY_DISPLAY_MS - elapsed));
                }

                setPaymentErrorMessage(
                  statusData?.cancellationReason || 'Payment was declined or cancelled by your bank.'
                );
                setPaymentConfirmState('failed');
                pollFinished = true;
                break;
              }
            }

            await new Promise((r) => setTimeout(r, INTERVAL_MS));
          }

          if (!pollFinished) {
            // Step 4b: Delayed confirmation fallback (bank delay)
            setPaymentConfirmState('delayed');
          }
        },
        modal: {
          ondismiss: async () => {
            setProcessingAction(null);
            if (!orderId) return;
            try {
              const statusResult = await getPaymentStatus(restaurantId, orderId);
              const alreadyPaid =
                statusResult?.data?.paymentStatus === 'completed' ||
                statusResult?.data?.fullyConfirmed === true;
              if (!alreadyPaid) {
                await updateOrderStatus(orderId, 'cancelled', restaurantId);
              }
            } catch {
              // Non-fatal cleanup
            }
          },
        },
      });

      rzp.on('payment.failed', (response) => {
        setProcessingAction(null);
        const reason =
          response.error?.description ||
          response.error?.reason ||
          'The transaction could not be processed by your bank or UPI provider.';
        setPaymentErrorMessage(reason);
        setPaymentConfirmState('failed');
      });

      rzp.open();
    } catch (err) {
      console.error('Razorpay launch error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      setProcessingAction(null);
    }
  };

  // "Pay & Order" mode: show email gate (skippable) → launch Razorpay checkout
  const handlePayAndOrderAction = () => {
    if (!capturedEmail) {
      setEmailGateAction('payAndOrder');
      setShowEmailGate(true);
    } else {
      launchRazorpayCheckout();
    }
  };

  const handlePayAndOrderEmailConfirmed = async (email) => {
    await signInAsGuestWithEmail(email);
    setShowEmailGate(false);
    launchRazorpayCheckout();
  };

  const handlePayAndOrderSkip = () => {
    setShowEmailGate(false);
    launchRazorpayCheckout();
  };


  const primaryColor = theme?.primary || '#000';
  const secondaryColor = theme?.secondary || '#fff';

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 pb-32">

      {/* Payment confirmation bottom sheet */}
      <PaymentStatusSheet
        isOpen={!!paymentConfirmState}
        state={paymentConfirmState}
        orderData={confirmedOrder}
        errorMessage={paymentErrorMessage}
        theme={theme}
        wasPaidFlow={wasPaidFlow}
        onRetry={() => {
          setPaymentConfirmState(null);
          launchRazorpayCheckout();
        }}
        onClose={() => setPaymentConfirmState(null)}
        onViewOrderDetails={() => {
          const targetOrderId = confirmedOrder?.id || confirmedOrder?.orderId || confirmedOrder?._id;
          if (targetOrderId) {
            navigate(`/restaurant/${restaurantId}/orders/${targetOrderId}`, { replace: true });
          } else {
            navigate(`/restaurant/${restaurantId}/orders`, { replace: true });
          }
        }}
      />

      <AlertDialog
        isOpen={!!error}
        onClose={() => setError(null)}
        title="Order Failed"
        message={error}
      />

      {/* Email Gate Modal */}
      <EmailGateModal
        isOpen={showEmailGate}
        allowSkip={emailGateAction === 'payAndOrder' ? allowPayAndOrderWithoutSignIn : false}
        onConfirm={emailGateAction === 'payAndOrder' ? handlePayAndOrderEmailConfirmed : handleEmailConfirmed}
        onSkip={emailGateAction === 'payAndOrder' ? handlePayAndOrderSkip : undefined}
        theme={theme}
      />

      {/* ─── Show Code Bottom Sheet ──────────────────────────────────────────── */}
      <AnimatePresence>
        {cartHoldCode && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-[60] backdrop-blur-sm"
              onClick={() => {
                 if (isClaimedSuccess) {
                   clearCart?.();
                   setCartHoldCode(null);
                   setActiveCartHoldId(null);
                   setIsClaimedSuccess(false);
                   navigate(`/restaurant/${restaurantId}`, { replace: true });
                 }
              }}
            />
            {/* Sheet */}
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-[70] px-6 pt-8 pb-safe overflow-hidden flex flex-col items-center"
              style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 24px)' }}
            >
              <div className="w-12 h-1.5 bg-gray-200 rounded-full absolute top-3 left-1/2 -translate-x-1/2" />
              
              <div className="w-full max-w-sm text-center pt-2">
                <div className="mb-6">
                  {isClaimedSuccess ? (
                    <>
                      <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-in zoom-in duration-300">
                        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-1">Order Claimed!</h2>
                      <p className="text-sm text-gray-500 max-w-[250px] mx-auto leading-relaxed">
                        Your order has been received by the cashier. Thanks for ordering!
                      </p>
                    </>
                  ) : (
                    <>
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">Show This Code</h2>
                      <p className="text-sm text-gray-500 px-4">
                        Please show this code to the waiter or cashier to complete your order.
                      </p>
                    </>
                  )}
                </div>
                
                {!isClaimedSuccess && (
                  <div className="bg-gray-100 border-2 border-gray-200/60 rounded-3xl p-8 mb-8 shadow-inner">
                    <div 
                      className="text-6xl font-black tracking-[0.25em] ml-[0.25em] text-gray-900"
                    >
                      {cartHoldCode}
                    </div>
                  </div>
                )}

                <button
                  onClick={() => {
                    clearCart?.();
                    setCartHoldCode(null);
                    setActiveCartHoldId(null);
                    setIsClaimedSuccess(false);
                    navigate(`/restaurant/${restaurantId}`, { replace: true });
                  }}
                  className="w-full py-4 px-4 rounded-2xl font-black text-[15px] uppercase tracking-wider transition-all shadow-[0_8px_20px_rgba(0,0,0,0.15)] active:scale-95"
                  style={{ backgroundColor: theme?.secondary || '#000', color: theme?.primary || '#fff' }}
                >
                  {isClaimedSuccess ? 'Done & Clear Cart' : 'Clear Cart & Close'}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Pay & Order is now handled inline by launchRazorpayCheckout — no stub overlay */}



      {/* Offers Sheet */}
      <OffersSheet
        isOpen={isOffersSheetOpen}
        onClose={() => setIsOffersSheetOpen(false)}
        promotions={promotions}
        appliedPromo={appliedPromo}
        onApply={(promo) => { handleApplyPromo(promo); setIsOffersSheetOpen(false); }}
        itemTotal={cartData.subtotal}
        theme={theme}
      />

       {/* Top Bar */}
      <div className="bg-white px-4 py-3 shadow-sm border-b sticky top-0 z-30 flex items-center">
         <button onClick={() => navigate('../', { relative: 'path' })} className="p-2 -ml-2 rounded-full hover:bg-gray-100">
            <Icon name="ArrowLeft" size={20} />
         </button>
         <h1 className="text-lg font-bold ml-2">Checkout</h1>
      </div>

      <div className="p-4 space-y-4">
         
         {/* 0. Promotion Carousel (if any) - Removed per request */}

         {/* 1. Order Type Chips */}
         {availableOrderTypes.length > 1 && (
           <div className="bg-white p-1 rounded-2xl shadow-sm border border-gray-100 flex gap-1">
              {availableOrderTypes.map(type => (
                  <button
                      key={type}
                      onClick={() => setDeliveryType(type)}
                      className={`flex-1 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-wider transition-all ${
                          deliveryType === type ? 'shadow-md translate-y-[-1px]' : 'text-gray-400 hover:bg-gray-50'
                      }`}
                      style={deliveryType === type && theme ? { backgroundColor: theme.secondary, color: theme.primary } : {}}
                  >
                      {type === 'takeaway' ? 'Pickup' : type.replace('_', ' ')}
                  </button>
              ))}
           </div>
         )}

         {/* 2. Cart Items */}
         <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
             <div className="p-4 border-b border-gray-50 flex justify-between items-center">
                 <h2 className="font-bold text-gray-900">Your Items</h2>
                 <button onClick={() => navigate('../', { relative: 'path' })} className="text-xs font-bold text-orange-600 bg-orange-50 px-3 py-1.5 rounded-full">
                    + Add more
                 </button>
             </div>
             <div className="divide-y divide-gray-50">
                 {cartData.items.map(item => (
                     <div key={item.cartItemId} className="p-4 flex items-start gap-3">
                          <div className="w-8 h-8 rounded-md bg-gray-100 overflow-hidden flex-shrink-0 shadow-sm border border-gray-50/50 mt-1">
                              {item.image_url ? (
                                <Image src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gray-50 text-gray-400">
                                  <Icon name={/(drink|beverage|juice|coffee|tea|shake|smoothie|water)/i.test(item.category || item.name) ? 'Coffee' : 'Utensils'} size={16} />
                                </div>
                              )}
                          </div>
                          <div className="flex-1">
                              <div className="flex justify-between items-start">
                                  <div>
                                      <h3 className="text-sm font-bold text-gray-900 line-clamp-2">{capitalizeFirst(item.name)}</h3>
                                      {/* Show effective unit price (base + addons) so the math matches the item's worth */}
                                      <p className="text-xs text-gray-500">
                                          {MoneyUtils.format(
                                              (item.price || 0) + 
                                              (item.customizations?.addons || item.customizations?.addOns || []).reduce(
                                                  (s, a) => s + (a.price || 0) * (a.quantity || 1), 0
                                              )
                                          )}
                                      </p>
                                  </div>
                                  <div className="flex items-center bg-white border border-gray-200 rounded-lg h-7 shadow-sm">
                                      <button 
                                         onClick={() => item.quantity > 1 ? updateQuantity(item.cartItemId, item.quantity - 1) : removeItem(item.cartItemId)}
                                         className="w-7 h-full flex items-center justify-center text-gray-500 hover:text-red-500"
                                      >-</button>
                                      <span className="w-4 text-center text-xs font-bold">{item.quantity}</span>
                                      <button 
                                         onClick={() => updateQuantity(item.cartItemId, item.quantity + 1)}
                                         className="w-7 h-full flex items-center justify-center text-gray-500 hover:text-green-600"
                                      >+</button>
                                  </div>
                              </div>
                              {item.customizations && (
                                  <div className="text-[10px] text-gray-400 mt-1 flex flex-col gap-0.5">
                                      {item.customizations.portion && <span>Size: {capitalizeFirst(item.customizations.portion.name || item.customizations.portion.size)}</span>}
                                      {(item.customizations.addons || item.customizations.addOns) && (
                                          <span>
                                              Add-ons:{' '}
                                              {(item.customizations.addons || item.customizations.addOns)
                                                  .map(a => `+${MoneyUtils.format(a.price || 0)} ${capitalizeFirst(a.name)}`)
                                                  .join(', ')}
                                          </span>
                                      )}
                                      {item.specialInstructions && <span className="italic">Note: {item.specialInstructions}</span>}
                                  </div>
                              )}
                          </div>
                     </div>
                 ))}
             </div>
         </div>

         {/* 3. Delivery / Table / Seat Info */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
              {deliveryType === 'dine_in' ? (() => {
                  const rawParts = [tableDetails?.tableLocation, tableDetails?.section].filter(Boolean);
                  const displayLocation = rawParts.length > 0 
                    ? rawParts.map(s => orderUtils.capitalizeWords(String(s).trim())).join(' • ') 
                    : (tableSection ? orderUtils.capitalizeWords(String(tableSection).trim()) : '');
                  return (
                    <div className="flex items-center justify-between">
                         <div className="flex items-center gap-3">
                             <div className="w-10 h-10 rounded-xl bg-orange-50 text-orange-600 border border-orange-100 flex items-center justify-center shrink-0">
                                 <Icon name="Armchair" size={20} />
                             </div>
                             <div>
                                 <div className="flex items-center gap-1.5 flex-wrap">
                                   <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                                     Dine-In Order
                                   </span>
                                   {displayLocation && (
                                     <span className="text-[10px] bg-gray-100 text-gray-700 font-semibold px-2 py-0.5 rounded-md border border-gray-200/50">
                                       {displayLocation}
                                     </span>
                                   )}
                                 </div>
                                 <p className="font-extrabold text-gray-900 text-base leading-tight mt-0.5">
                                   {tableNumber ? orderUtils.formatTableDisplayName(tableNumber, isTrackedTable) : 'Dine In / Counter'}
                                 </p>
                             </div>
                         </div>
                     </div>
                  );
              })() : deliveryType === 'takeaway' ? (
                 <div className="flex items-center gap-3">
                     <div className="bg-blue-100 p-2.5 rounded-full text-blue-600">
                         <Icon name="ShoppingBag" size={20} />
                     </div>
                     <div>
                         <p className="text-xs font-medium text-gray-500 uppercase">Self Pickup</p>
                         <p className="font-bold text-gray-900 text-sm">Order will be packed for takeaway</p>
                     </div>
                 </div>
             ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 pb-2 border-b border-gray-100">
                    <div className="w-9 h-9 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                      <Icon name="Bike" size={20} />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-900">Home Delivery</p>
                      <p className="text-[11px] text-gray-500">Contact & delivery address</p>
                    </div>
                  </div>

                  <DeliveryAddressSection
                    restaurantId={restaurantId}
                    phone={phone}
                    onPhoneChange={setPhone}
                    recipientName={recipientName}
                    onRecipientNameChange={setRecipientName}
                    selectedAddress={deliveryAddress}
                    onSelectAddress={setDeliveryAddress}
                    theme={theme}
                  />
                </div>
             )}
         </div>

         {/* 4. Offers & Promotions */}
         <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
           <div className="flex items-center justify-between">
             <div className="flex items-center gap-3">
               <div className="p-2 rounded-full" style={theme ? { backgroundColor: `${theme.secondary}15`, color: theme.secondary } : { backgroundColor: '#f3f4f6', color: '#111' }}>
                 <Icon name="Tag" size={18} />
               </div>
               <div>
                 <h3 className="font-bold text-gray-900 text-sm">Offers & Promotions</h3>
                 {appliedPromo ? (
                   <p className="text-xs font-semibold" style={{ color: theme?.secondary || '#111' }}>
                     {formatPromoLabel(appliedPromo)} applied — saving {MoneyUtils.format(discountAmount)}
                   </p>
                 ) : (
                   <p className="text-xs text-gray-500">
                     {promotions.length > 0
                       ? `${promotions.length} offer${promotions.length > 1 ? 's' : ''} available`
                       : 'No promo applied'}
                   </p>
                 )}
               </div>
             </div>
             {appliedPromo ? (
               <button
                 onClick={handleRemovePromo}
                 className="text-xs font-bold text-red-500 hover:text-red-700 px-3 py-1.5 rounded-lg border border-red-200 bg-red-50 transition-colors"
               >
                 Remove
               </button>
             ) : (
               <button
                 onClick={() => setIsOffersSheetOpen(true)}
                 className="text-xs font-bold px-3 py-1.5 rounded-lg transition-colors border"
                 style={
                   theme
                     ? { borderColor: theme.secondary, color: theme.secondary, backgroundColor: `${theme.secondary}10` }
                     : { borderColor: '#111', color: '#111', backgroundColor: '#f9fafb' }
                 }
               >
                 {promotions.length > 0 ? 'View Offers' : 'Apply Code'}
               </button>
             )}
           </div>
         </div>

         {/* 5. Tip Section */}
           <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
                <div className="flex justify-between items-center mb-3">
                    <h3 className="font-bold text-gray-900 flex items-center">
                        <span className="mr-2">❤️</span> Tip your server
                    </h3>
                    {tipAmount > 0 && (
                        <button 
                          onClick={() => { setTipAmount(0); setCustomTip(''); }}
                          className="text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-full transition-all"
                        >
                            Clear
                        </button>
                    )}
                </div>
                <div className="flex gap-2 flex-wrap">
                    {[2000, 5000, 10000].map(amt => (
                        <TipChip 
                          key={amt} 
                          amount={amt} 
                          selected={tipAmount === amt} 
                          onClick={() => { setTipAmount(amt); setCustomTip(''); }} 
                       />
                    ))}
                    <div className="relative flex-1 min-w-[80px]">
                        <input 
                          type="number" 
                          placeholder="Custom" 
                          value={customTip}
                          onChange={(e) => {
                              const val = e.target.value;
                              setCustomTip(val);
                              setTipAmount(val ? Number(val) * 100 : 0);
                          }}
                          className={`w-full px-4 py-2 rounded-xl text-sm font-medium border transition-all text-center focus:ring-1 focus:ring-black focus:border-black outline-none ${tipAmount > 0 && ![2000,5000,10000].includes(tipAmount) ? 'bg-black text-white border-black' : 'bg-white border-gray-200 text-gray-900'}`}
                        />
                    </div>
                </div>
                {tipAmount > 0 && (
                   <p className="text-xs text-gray-500 mt-2 text-center">
                      Thank you! {MoneyUtils.format(tipAmount)} added to the bill.
                   </p>
                )}
           </div>


         {/* 6. Bill Summary */}
         <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-2">
             <h3 className="font-bold text-gray-900 text-sm mb-3">Bill Details</h3>
             <BillRow label="Item Total" value={MoneyUtils.format(cartData.subtotal)} />
             {cartData.tax > 0 && <BillRow label="Taxes & Charges" value={MoneyUtils.format(cartData.tax)} />}
             {deliveryFee > 0 && <BillRow label="Delivery Fee" value={MoneyUtils.format(deliveryFee)} />}
             {tipAmount > 0 && <BillRow label="Tip" value={MoneyUtils.format(tipAmount)} color="text-green-600" />}
             {discountAmount > 0 && (
               <BillRow
                 label={`Discount (${appliedPromo?.title || 'Promo'})`}
                 value={`-${MoneyUtils.format(discountAmount)}`}
                 color="text-green-600"
               />
             )}
             <BillRow label="To Pay" value={MoneyUtils.format(grandTotal)} isTotal isBold />
             {discountAmount > 0 && (
               <p className="text-xs text-green-600 font-semibold text-right mt-1">
                 🎉 You're saving {MoneyUtils.format(discountAmount)}!
               </p>
             )}
         </div>

      </div>

      {/* ─── Premium Bottom Action Bar ────────────────────────────────────────── */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-gray-100 z-40 pb-safe px-4 pt-4 shadow-[0_-10px_40px_rgba(0,0,0,0.06)] rounded-t-3xl"
           style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' }}>
        
        {capabilities?.isViewOnly ? (
          <div className="flex flex-col items-center py-2 text-center">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
              Digital Menu Only • Online Ordering Disabled
            </span>
            <p className="text-[11px] text-gray-400 mt-0.5">
              Please call staff to place your order.
            </p>
          </div>
        ) : (
          (() => {
            const hasShowCode = capabilities?.canShowCode;
            const hasOrder = capabilities?.canOrder;
            const hasPayAndOrder = capabilities?.canPayAndOrder;
            const hasBothOrderMethods = hasOrder && hasPayAndOrder;
            
            const activeActions = [];
            if (hasShowCode) activeActions.push('showCode');
            if (hasOrder) activeActions.push('order');
            if (hasPayAndOrder) activeActions.push('payAndOrder');

            if (activeActions.length === 0) return null;

            // Determine if we need the payment toggle
            const showToggle = hasBothOrderMethods;

            return (
              <div className="flex flex-col gap-2">
                {/* Minimal Price Details Header */}
                <div className="flex items-center justify-between px-1 mb-1">
                  <span className="text-xs text-gray-500 font-medium">
                    {deliveryType === 'dine_in' 
                      ? (tableNumber ? orderUtils.formatTableDisplayName(tableNumber, isTrackedTable) : 'Dine In') 
                      : 'To Pay'} • <span className="font-bold text-gray-700">{cartData.items.length} items</span>
                  </span>
                  <span className="text-xl font-black text-gray-900 leading-none">
                    {MoneyUtils.format(grandTotal)}
                  </span>
                </div>

                {/* Payment Method Toggle (If both Pay & Order and Order are enabled) */}
                {showToggle && (
                  <div className="flex bg-gray-100 p-1 rounded-xl mb-1">
                    <button
                      onClick={() => setPaymentMethod('online')}
                      className={`flex-1 py-2 text-[11px] font-bold rounded-lg transition-all uppercase tracking-wider ${
                        paymentMethod === 'online' ? 'bg-white shadow-sm text-gray-900 border border-gray-200/60' : 'text-gray-400 hover:text-gray-700'
                      }`}
                    >
                      Pay Online
                    </button>
                    <button
                      onClick={() => setPaymentMethod('counter')}
                      className={`flex-1 py-2 text-[11px] font-bold rounded-lg transition-all uppercase tracking-wider ${
                        paymentMethod === 'counter' ? 'bg-white shadow-sm text-gray-900 border border-gray-200/60' : 'text-gray-400 hover:text-gray-700'
                      }`}
                    >
                      {deliveryType === 'delivery' ? 'Cash on Delivery' : 'Pay at Counter'}
                    </button>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center gap-3">
                  {/* Show Code (Secondary) */}
                  {hasShowCode && (
                    <button
                      onClick={handleShowCodeAction}
                      disabled={processingAction !== null}
                      className="flex-[0.6] py-3.5 px-2 rounded-2xl text-[12px] font-black disabled:opacity-50 transition-all flex items-center justify-center uppercase tracking-wider scale-wrapper active:scale-95 bg-white border-[1.5px]"
                      style={{ color: '#000', borderColor: '#e5e7eb' }}
                    >
                      {processingAction === 'showCode' ? 'Wait...' : 'Show Code'}
                    </button>
                  )}

                  {/* Submit Order (Primary) */}
                  {(hasOrder || hasPayAndOrder) && (
                    <button
                      onClick={() => {
                        if (showToggle) {
                          paymentMethod === 'online' ? handlePayAndOrderAction() : handleOrderModeAction();
                        } else {
                          // Only one is available
                          hasPayAndOrder ? handlePayAndOrderAction() : handleOrderModeAction();
                        }
                      }}
                      disabled={processingAction !== null}
                      className="flex-1 py-3.5 px-2 rounded-2xl text-[13px] font-black disabled:opacity-50 transition-all flex items-center justify-center uppercase tracking-wider scale-wrapper active:scale-95 border-[1.5px]"
                      style={theme ? { backgroundColor: theme.secondary, color: theme.primary, borderColor: theme.secondary } : { backgroundColor: '#000', color: '#fff', borderColor: '#000' }}
                    >
                      {processingAction === 'payAndOrder' || processingAction === 'order' ? 'Wait...' : 'Submit Order'}
                    </button>
                  )}
                </div>

                {/* Subtle Footer Note */}
                {!showToggle && (
                  <p className="text-[9px] text-center text-gray-400 mt-1 uppercase tracking-[0.2em] font-medium opacity-70">
                     {hasPayAndOrder && !hasOrder ? 'Pay Online Securely' : (deliveryType === 'dine_in' ? 'Pay at Counter / Table' : 'Pay on Delivery / Pickup')}
                  </p>
                )}
              </div>
            );
          })()
        )}

      </div>

    </div>
  );
};

export default CheckoutFlow;